import { BaseComponent } from '../core/BaseComponent';

export interface AdvancedTableOptions {
  sortable: boolean;
  searchable: boolean;
  paginated: boolean;
  pageSize: number;
  loading: boolean;
  onSort?: (detail: any) => void;
  onSearch?: (query: string) => void;
  onPageChange?: (page: number) => void;
}

export class AdvancedTable extends BaseComponent<AdvancedTableOptions> {
  private thead: HTMLElement | null = null;
  private tbody: HTMLElement | null = null;
  private originalRows: HTMLTableRowElement[] = [];
  private filteredRows: HTMLTableRowElement[] = [];
  private currentPage: number = 1;
  private searchInput: HTMLInputElement | null = null;
  private paginationContainer: HTMLElement | null = null;

  protected getDefaultOptions(): AdvancedTableOptions {
    return {
      sortable: true,
      searchable: true,
      paginated: true,
      pageSize: 10,
      loading: false
    };
  }

  protected init(): void {
    super.init();
    this.thead = this.element.querySelector('thead');
    this.tbody = this.element.querySelector('tbody');
    if (!this.tbody) return;

    this.originalRows = Array.from(this.tbody.querySelectorAll('tr'));
    this.filteredRows = [...this.originalRows];
    
    const container = this.element.closest('.table-container') as HTMLElement;
    this.searchInput = container?.querySelector('.table-search .search-input');
    this.paginationContainer = container?.querySelector('.table-pagination');
    
    if (this.options.sortable) this.initSorting();
    if (this.options.searchable) this.initSearching();
    
    this.updateDisplay();
  }

  private initSorting(): void {
    if (!this.thead) return;
    const headers = this.thead.querySelectorAll<HTMLElement>('th.sortable');
    headers.forEach((th, index) => {
      th.addEventListener('click', () => this.handleSort(th, index));
    });
  }

  private handleSort(th: HTMLElement, columnIndex: number): void {
    const isAsc = th.classList.contains('sort-asc');
    const direction = isAsc ? 'desc' : 'asc';

    this.thead?.querySelectorAll('th.sortable').forEach(h => {
      h.classList.remove('sort-asc', 'sort-desc');
    });

    th.classList.add(`sort-${direction}`);

    this.filteredRows.sort((a, b) => {
      const aText = a.cells[columnIndex].textContent?.trim().toLowerCase() || '';
      const bText = b.cells[columnIndex].textContent?.trim().toLowerCase() || '';
      
      const aVal = parseFloat(aText);
      const bVal = parseFloat(bText);

      if (!isNaN(aVal) && !isNaN(bVal)) {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      return direction === 'asc' 
        ? aText.localeCompare(bText) 
        : bText.localeCompare(aText);
    });

    if (this.options.onSort) this.options.onSort({ column: th, direction });
    this.emit('sort', { column: th, direction });
    
    this.currentPage = 1;
    this.updateDisplay();
  }

  private initSearching(): void {
    if (!this.searchInput) return;
    this.searchInput.addEventListener('input', (e) => {
      const query = (e.target as HTMLInputElement).value.toLowerCase();
      this.handleSearch(query);
    });
  }

  private handleSearch(query: string): void {
    this.filteredRows = this.originalRows.filter(row => {
      return Array.from(row.cells).some(cell => 
        cell.textContent?.toLowerCase().includes(query)
      );
    });

    if (this.options.onSearch) this.options.onSearch(query);
    this.emit('search', query);

    this.currentPage = 1;
    this.updateDisplay();
  }

  public updateDisplay(): void {
    if (!this.tbody) return;
    this.tbody.innerHTML = '';
    
    let rowsToDisplay = this.filteredRows;
    
    if (this.options.paginated) {
      const start = (this.currentPage - 1) * this.options.pageSize;
      const end = start + this.options.pageSize;
      rowsToDisplay = (this.filteredRows.slice(start, end) as any);
      this.renderPagination();
    }

    if (rowsToDisplay.length === 0) {
      this.renderEmptyState();
    } else {
      rowsToDisplay.forEach(row => this.tbody!.appendChild(row));
    }
  }

  private renderPagination(): void {
    if (!this.paginationContainer) return;
    
    const totalPages = Math.ceil(this.filteredRows.length / this.options.pageSize);
    const info = this.paginationContainer.querySelector('.pagination-info');
    const controls = this.paginationContainer.querySelector('.pagination-controls');
    
    if (info) {
      const start = (this.currentPage - 1) * this.options.pageSize + 1;
      const end = Math.min(this.currentPage * this.options.pageSize, this.filteredRows.length);
      info.textContent = `Showing ${this.filteredRows.length ? start : 0} to ${end} of ${this.filteredRows.length} entries`;
    }

    if (controls) {
      controls.innerHTML = '';
      
      const prevBtn = this.createPageLink('Previous', this.currentPage > 1);
      prevBtn.addEventListener('click', () => this.goToPage(this.currentPage - 1));
      controls.appendChild(prevBtn);

      for (let i = 1; i <= totalPages; i++) {
        const pageBtn = this.createPageLink(i.toString(), true, i === this.currentPage);
        pageBtn.addEventListener('click', () => this.goToPage(i));
        controls.appendChild(pageBtn);
      }

      const nextBtn = this.createPageLink('Next', this.currentPage < totalPages);
      nextBtn.addEventListener('click', () => this.goToPage(this.currentPage + 1));
      controls.appendChild(nextBtn);
    }
  }

  private createPageLink(text: string, enabled: boolean, active: boolean = false): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = `btn btn-outline btn-sm ${active ? 'active' : ''}`;
    btn.textContent = text;
    if (!enabled) btn.disabled = true;
    return btn;
  }

  private goToPage(page: number): void {
    if (page < 1 || page > Math.ceil(this.filteredRows.length / this.options.pageSize)) return;
    this.currentPage = page;
    this.updateDisplay();
    if (this.options.onPageChange) this.options.onPageChange(page);
    this.emit('pageChange', page);
  }

  private renderEmptyState(): void {
    const colCount = this.thead?.querySelectorAll('th').length || 1;
    const tr = document.createElement('tr');
    tr.innerHTML = `
      <td colspan="${colCount}" style="text-align: center; padding: 2rem; color: #666;">
        <div>No data found</div>
      </td>
    `;
    this.tbody?.appendChild(tr);
  }
}
